import type {
    IPenciledInputs,
    IPencilOfferInputs,
    ItemizedDiscount,
    Offer,
    OfferType,
    PaymentSchedule,
    SpecialPaymentFormula
} from '@makemydeal/dr-platform-types';
import type { OfferReduxBaseState } from '../types/offerReduxBaseState';
import { PaymentScheduleTypes } from '@makemydeal/dr-platform-types';
import { createSelector } from 'reselect';

const EMPTY_DEALER_DISCOUNTS: ItemizedDiscount[] = [];

export function getOfferPenciledInputs(offer: Offer): IPenciledInputs {
    return offer.penciledInputs || ({} as IPenciledInputs);
}

export const getSavedInputs = (state: IPenciledInputs): OfferReduxBaseState => {
    return state.savedInputs || {};
};

export const getSavedOfferType = (state: IPenciledInputs): OfferType => {
    return getSavedInputs(state).offerType;
};

export const getSavedSpecialPaymentFormula = (state: IPenciledInputs): SpecialPaymentFormula => {
    return getSavedInputs(state).specialPaymentFormula;
};

export const getDealerDiscounts = (state: IPenciledInputs): ItemizedDiscount[] => {
    return state.dealerDiscounts || EMPTY_DEALER_DISCOUNTS;
};

export const getOfferTypeTermId = (state: IPenciledInputs, offerType: OfferType): string | undefined => {
    return state.byOfferType![offerType].term;
};

export const getOfferDownPayment = (state: IPencilOfferInputs): number | undefined => {
    return state.downPayment;
};

export const getInputPaymentFrequency = (state: IPencilOfferInputs): PaymentSchedule | undefined => {
    return state?.paymentFrequency;
};

export const isInputOnePayLease = (state: OfferReduxBaseState): boolean => {
    const paymentFrequency = getInputPaymentFrequency(state);
    return paymentFrequency === PaymentScheduleTypes.OnePayLease;
};

export const getDealerDiscountsTotalAmount = createSelector([getDealerDiscounts], (dealerDiscounts: ItemizedDiscount[]): number => {
    return dealerDiscounts.reduce((total, discount) => total + (discount.discountAmount || 0), 0);
});

/**
 * Calculates the discounted offer price based on current offer price and dealer discounts
 * @param state - The penciled inputs state object
 * @param dealerDiscounts - Array of itemized dealer discounts
 * @param offerType - Type of offer being processed
 * @returns The final discounted offer price after applying all dealer discounts
 */
export const getDiscountedOfferPrice = (
    state: IPenciledInputs,
    newDealerDiscounts: ItemizedDiscount[],
    offerType: OfferType
): number => {
    const currentOfferPrice = state.byOfferType![offerType]?.offerPrice || 0;
    // currentDiscountTotal is the total of dealer discounts from the state (persisted or previously applied)
    const penciledInputsDealerDiscountsTotal = getDealerDiscountsTotalAmount(state);
    // discountTotal is the total of dealer discounts passed in as an argument (newly calculated or edited)
    const newDealerDiscountsTotal = newDealerDiscounts
        ? newDealerDiscounts.reduce((total, discount) => total + (discount.discountAmount || 0), 0)
        : 0;
    const OrginalOfferPrice = currentOfferPrice + penciledInputsDealerDiscountsTotal; // Get original price before discounts

    return OrginalOfferPrice - newDealerDiscountsTotal; // Apply new discounts
};
